import { component, OnInit, prop } from '@gbds/core/src/ts/core'
import { PriceShortCodes, DisclosureShortCodes } from '../../../models/shortcodes'
import { RegionalDisclosure } from '../regional-disclosure/regional-disclosure'
import { RegionalPrice } from '../regional-price/regional-price'

interface RegionalData {
	[key: string]: {
		asShownPrice: string
		startingPriceDisclosure: string
		asShownPriceDisclosure: string
		startingPrice: string
		label: string
	}
}

/**
 * @noInheritDoc
 */
@component('gb-dynamic-text')
export class DynamicText extends HTMLElement implements OnInit {
	/**
	 * JSON object that contains the pricing and disclosures for each region. Must also contain default values as a fallback.
	 */
	@prop('object')
	private regionalInformationJson: RegionalData

	private _dynamicParagraphText: HTMLElement

	constructor() {
		super()
	}

	onInit() {
		this._dynamicParagraphText = this.querySelector('.gb-dynamic-paragraph-text')
		this._replaceShortcodes(this._dynamicParagraphText)
		this._dynamicParagraphText.classList.remove('hide')
	}

	/**
	 * Method called when the element is removed from the DOM.  Need to remove event handlers.
	 */
	disconnectedCallback() {
		// do nothing
	}

	/**
	 * Replace predefined shortcode values with dynamic elements
	 */
	private _replaceShortcodes(paragraphText: HTMLElement) {
		// A dynamic paragraph element can have multiple <p> elements within it.
		const paragraphElements = paragraphText.querySelectorAll('p')

		paragraphElements.forEach(p => {
			Object.values(PriceShortCodes).forEach(pshortcode => {
				if (p.textContent?.indexOf(pshortcode) > -1) {
					const priceKey = this._convertToCamelCase(pshortcode.replace('{{', '').replace('}}', ''), '_')
					const gbRegionalPriceElem = new RegionalPrice()
					const shortcodeRegex = new RegExp(pshortcode, 'g')

					Object.keys(this.regionalInformationJson).forEach(entry => {
						if (this.regionalInformationJson[entry][priceKey])
							gbRegionalPriceElem.setAttribute(
								entry.toLowerCase() + '-price',
								this.regionalInformationJson[entry][priceKey]
							)
					})

					gbRegionalPriceElem.classList.add('hide')
					gbRegionalPriceElem.textContent = pshortcode

					this._dynamicParagraphText.innerHTML = this._dynamicParagraphText.innerHTML.replace(
						shortcodeRegex,
						gbRegionalPriceElem.outerHTML
					)
				}
			})

			Object.values(DisclosureShortCodes).forEach(dshortcode => {
				if (p.textContent?.indexOf(dshortcode) > -1) {
					const disclosureKey = this._convertToCamelCase(dshortcode.replace('{{', '').replace('}}', ''), '_')
					const gbRegionalDisclosureElem = new RegionalDisclosure()
					const shortcodeRegex = new RegExp(dshortcode, 'g')

					Object.keys(this.regionalInformationJson).forEach(entry => {
						if (this.regionalInformationJson[entry][disclosureKey])
							gbRegionalDisclosureElem.setAttribute(
								entry.toLowerCase() + '-disclosure',
								this.regionalInformationJson[entry][disclosureKey]
							)
					})

					gbRegionalDisclosureElem.classList.add('hide')
					gbRegionalDisclosureElem.textContent = dshortcode

					this._dynamicParagraphText.innerHTML = this._dynamicParagraphText.innerHTML.replace(
						shortcodeRegex,
						gbRegionalDisclosureElem.outerHTML
					)
				}
			})
		})
	}

	/**
	 *
	 * @param str The string you are trying to convert to camel case
	 * @param delimiter The value that is acting as a delimiter currently. (Ex. foo-bar '-' is the delimiter)
	 * @returns A string that has removed the previous delimiter and is now camel case
	 */
	private _convertToCamelCase(str: string, delimiter: string) {
		const regex = new RegExp(`${delimiter}([a-z])`, 'g')

		return str.replace(regex, function (g) {
			return g[1].toUpperCase()
		})
	}
}
